import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Pencil, Trash2, Plus, Save, ChevronDown, ChevronLeft, ChevronRight,
  Copy, Check, Download, AlertTriangle, X, FileSpreadsheet, CheckCircle2,
  BookOpen, ListChecks, Key, Calendar, Layers, Sparkles,
} from "lucide-react";
import { ColorRing } from "react-loader-spinner";
import moment from "moment-jalaali";
import {
  getKnowledgeChunks, addKnowledgeChunk, updateKnowledgeChunk,
  deleteKnowledgeChunk, downloadKnowledgeChunksExcel,
} from "./services/liteelwordInfo";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "./components/UserSidebar";
import Header from "units/dashboard/components/Header";
import { useTranslation } from "react-i18next";

const DATA_TYPES = [
  { id: "text", name: "txt" },
  { id: "faq", name: "json" },
  { id: "product", name: "csv" },
  { id: "policy", name: "tabel" },
];

const ITEMS_PER_PAGE = 4;
const MAX_DOWNLOAD_CHUNKS = 2000;

/* ================================================================
 *  Loader helper
 * ================================================================ */
const WhiteLoader = () => (
  <ColorRing
    visible
    height="20"
    width="20"
    ariaLabel="loading"
    colors={["#fff", "#fff", "#fff", "#fff", "#fff"]}
  />
);

/* ================================================================
 *  Main Component
 * ================================================================ */
function LittelInfoInner() {
  const { t } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const [list, setList] = useState([]);
  const [contentCopied, setContentCopied] = useState(false);
  const dropdownRef = useRef(null);

  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [content, setContent] = useState("");

  const [isListLoading, setIsListLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadedOnce, setHasDownloadedOnce] = useState(false);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedCount, setSelectedCount] = useState(null);
  const [selectedCountLabel, setSelectedCountLabel] = useState("");

  const formRef = useRef(null);
  const keywordInputRef = useRef(null);
  const modalRef = useRef(null);

  /* ---------- DOWNLOAD_OPTIONS با ترجمه ---------- */
  const DOWNLOAD_OPTIONS = useMemo(() => [
    { label: t("littleInfo.downloadOptions.last100"), value: 100 },
    { label: t("littleInfo.downloadOptions.last200"), value: 200 },
    { label: t("littleInfo.downloadOptions.last300"), value: 300 },
    { label: t("littleInfo.downloadOptions.last500"), value: 500 },
    { label: t("littleInfo.downloadOptions.last1000"), value: 1000 },
    { label: t("littleInfo.downloadOptions.last2000"), value: 2000 },
    { label: t("littleInfo.downloadOptions.all"), value: null },
  ], [t]);

  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  useEffect(() => {
    if (accessToken) getList();
  }, [accessToken]);

  const getList = async () => {
    setIsListLoading(true);
    try {
      const res = await getKnowledgeChunks(accessToken);
      if (res?.ok) setList(res.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setIsListLoading(false);
    }
  };

  /* ---------- Download Modal ---------- */
  const handleOpenDownloadModal = () => {
    if (hasDownloadedOnce) {
      toast.warning(t("littleInfo.toasts.alreadyDownloaded"));
      return;
    }
    if (!accessToken) {
      toast.error(t("littleInfo.toasts.notAuthenticated"));
      return;
    }
    if (list.length === 0) {
      toast.warning(t("littleInfo.toasts.noDataToDownload"));
      return;
    }
    setShowDownloadModal(true);
    setSelectedCount(100);
    setSelectedCountLabel(t("littleInfo.downloadOptions.last100"));
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setSelectedCount(null);
    setSelectedCountLabel("");
  };

  const handleSelectCount = (value, label) => {
    setSelectedCount(value);
    setSelectedCountLabel(label);
  };

  const handleConfirmDownload = async () => {
    let downloadCount = selectedCount;
    if (selectedCount !== null && selectedCount > list.length) {
      toast.warning(
        t("littleInfo.toasts.countTooLarge", {
          total: list.length,
          selected: selectedCount,
        })
      );
      downloadCount = null;
    }

    setIsDownloading(true);
    setShowDownloadModal(false);

    try {
      const result = await downloadKnowledgeChunksExcel(accessToken, downloadCount);
      if (result?.ok && result.data) {
        const blob = result.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        let filename = `data_chunks_${downloadCount || "all"}.xlsx`;
        const cd = result.headers?.["content-disposition"];
        if (cd) {
          const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (m && m[1]) filename = m[1].replace(/['"]/g, "");
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
        toast.success(
          t("littleInfo.toasts.downloadSuccess", {
            scope: downloadCount
              ? t("littleInfo.toasts.downloadSuccessCount", { count: downloadCount })
              : t("littleInfo.toasts.downloadSuccessAll"),
          })
        );
        setHasDownloadedOnce(true);
      }
    } catch (error) {
      console.error("Unexpected error in download handler:", error);
      toast.error(t("littleInfo.toasts.downloadError"));
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowDownloadModal(false);
      }
    };
    if (showDownloadModal) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDownloadModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- Form Logic ---------- */
  const resetForm = () => {
    setKeyword("");
    setContent("");
    setSelectedItem(null);
    setEditId(null);
  };

  const handleEdit = (item) => {
    setKeyword(item.keyword || "");
    setContent(item.content || "");
    setSelectedItem(DATA_TYPES.find((d) => d.id === item.dataType) || null);
    setEditId(item.id);
    setOpen(false);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      keywordInputRef.current?.focus();
    });
  };

  const handleDelete = (id) => setDeleteTarget(id);
  const cancelDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    const id = deleteTarget;
    if (!id) return;
    setDeletingId(id);
    try {
      const res = await deleteKnowledgeChunk({ accessToken, entryId: id });
      if (res?.ok) {
        toast.success(t("littleInfo.toasts.deleteSuccess"));
        setList((prev) => prev.filter((item) => item.id !== id));
        if (editId === id) resetForm();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setDeletingId(null);
      setDeleteTarget(null);
    }
  };

  const validateForm = () => {
    if (!keyword.trim() || !content.trim()) {
      toast.error(t("littleInfo.toasts.fieldsRequired"));
      return false;
    }
    if (!selectedItem) {
      toast.error(t("littleInfo.toasts.dataTypeRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      if (editId) {
        const res = await updateKnowledgeChunk({
          accessToken, entryId: editId, keyword, dataType: selectedItem.id, content,
        });
        if (res?.ok) {
          const now = new Date().toISOString();
          toast.success(t("littleInfo.toasts.editSuccess"));
          setList((prev) =>
            prev.map((item) =>
              item.id === editId
                ? { ...item, keyword, content, dataType: selectedItem.id, updatedAt: res.data?.updatedAt || now }
                : item
            )
          );
          resetForm();
          setCurrentPage(1);
          getList();
        }
      } else {
        const res = await addKnowledgeChunk({ accessToken, keyword, dataType: selectedItem.id, content });
        if (res?.ok) {
          toast.success(t("littleInfo.toasts.addSuccess"));
          resetForm();
          setCurrentPage(1);
          getList();
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyContent = async () => {
    if (!content.trim()) {
      toast.error(t("littleInfo.toasts.noTextToCopy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(content);
      setContentCopied(true);
      toast.success(t("littleInfo.toasts.copySuccess"));
      setTimeout(() => setContentCopied(false), 1500);
    } catch (err) {
      console.log(err);
      toast.error(t("littleInfo.toasts.copyError"));
    }
  };

  /* ---------- Sorting & Pagination ---------- */
  const sortedList = useMemo(
    () =>
      [...list].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      ),
    [list]
  );

  const totalPages = Math.max(1, Math.ceil(sortedList.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedList.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedList, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const isDownloadDisabled = () =>
    isDownloading || list.length === 0 || hasDownloadedOnce;

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        {/* Main */}
        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-4 sm:py-6">

              {/* ============ Page Header ============ */}
              <div className="max-w-5xl mx-auto mb-6">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <BookOpen size={20} className="text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:from-amber-200 dark:via-teal-300 dark:to-teal-400 bg-clip-text text-transparent">
                      {t("littleInfo.pageTitle")}
                    </h1>
                    <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 mt-1 leading-6">
                      {t("littleInfo.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ============ FORM CARD ============ */}
              <div
                ref={formRef}
                className="
                  relative max-w-5xl mx-auto mb-5
                  rounded-2xl
                  bg-white/70 dark:bg-slate-900/70
                  backdrop-blur-xl
                  border border-teal-500/15 dark:border-teal-400/15
                  shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)]
                  dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
                  overflow-hidden
                "
              >
                {/* تزئینات */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                  <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-300/10 blur-3xl dark:bg-amber-400/5" />
                  <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl dark:bg-teal-500/5" />
                </div>

                <div className="relative p-4 sm:p-6">
                  {/* Edit header */}
                  {editId && (
                    <div className="mb-4 flex items-center justify-between rounded-xl bg-amber-400/15 border border-amber-300/40 px-3 py-2">
                      <span className="flex items-center gap-2 text-xs sm:text-sm font-bold text-amber-800 dark:text-amber-200">
                        <Sparkles size={14} className="text-amber-500" />
                        {t("littleInfo.editingItem")}
                      </span>
                      <button
                        onClick={resetForm}
                        className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-amber-800 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-amber-300/40 hover:bg-amber-300/20 transition-colors"
                      >
                        {t("littleInfo.cancelEdit")}
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">

                    {/* Data Type Dropdown */}
                    <div ref={dropdownRef} className="relative">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                        {t("littleInfo.dataTypeLabel")}
                      </label>
                      <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className={`
                          w-full flex justify-between items-center gap-2
                          px-4 py-3 rounded-xl text-sm
                          transition-all duration-300
                          ${open
                            ? "bg-amber-300/10 border-amber-300/60 text-slate-800 dark:text-amber-100 ring-2 ring-amber-300/40"
                            : "bg-white/70 dark:bg-slate-800/60 border-teal-500/20 dark:border-teal-400/20 text-slate-700 dark:text-slate-200 hover:border-amber-300/60"
                          }
                          border
                        `}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Layers size={14} className="shrink-0 text-teal-600/80 dark:text-teal-300/80" />
                          <span className={`truncate ${selectedItem ? "font-semibold" : "text-slate-400 dark:text-slate-500"}`}>
                            {selectedItem ? selectedItem.name : t("littleInfo.dataTypePlaceholder")}
                          </span>
                        </span>
                        <ChevronDown
                          size={16}
                          className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                        />
                      </button>

                      {open && (
                        <div className="absolute mt-2 w-full rounded-xl overflow-hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-teal-500/25 dark:border-teal-400/20 shadow-[0_20px_60px_-20px_rgba(15,118,110,0.5)] z-50">
                          <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                          {DATA_TYPES.map((item) => (
                            <button
                              type="button"
                              key={item.id}
                              onClick={() => {
                                setSelectedItem(item);
                                setOpen(false);
                              }}
                              className={`
                                w-full text-right px-4 py-3 text-sm transition-colors
                                ${selectedItem?.id === item.id
                                  ? "bg-teal-500/10 text-teal-800 dark:text-teal-200 font-bold"
                                  : "text-slate-700 dark:text-slate-200 hover:bg-amber-300/10"
                                }
                              `}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Keyword */}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                        {t("littleInfo.keywordLabel")}
                      </label>
                      <div className="relative">
                        <Key size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-600/80 dark:text-teal-300/80 pointer-events-none" />
                        <input
                          ref={keywordInputRef}
                          type="text"
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          placeholder={t("littleInfo.keywordPlaceholder")}
                          className="
                            w-full pr-9 pl-4 py-3 rounded-xl text-sm
                            bg-white/70 dark:bg-slate-800/60
                            text-slate-800 dark:text-slate-100
                            border border-teal-500/20 dark:border-teal-400/20
                            focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                            transition-all duration-200
                            placeholder:text-slate-400 dark:placeholder:text-slate-500
                          "
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                        {t("littleInfo.contentLabel")}
                      </label>
                      <div className="relative">
                        <textarea
                          rows={4}
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder={t("littleInfo.contentPlaceholder")}
                          className="
                            w-full rounded-xl px-4 py-3 pl-11 text-sm resize-y min-h-[110px]
                            bg-white/70 dark:bg-slate-800/60
                            text-slate-800 dark:text-slate-100
                            border border-teal-500/20 dark:border-teal-400/20
                            focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                            transition-all duration-200
                            placeholder:text-slate-400 dark:placeholder:text-slate-500
                          "
                        />
                        <button
                          type="button"
                          onClick={handleCopyContent}
                          className="absolute top-2.5 left-2.5 h-8 w-8 flex items-center justify-center rounded-lg text-teal-600 dark:text-teal-300 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200"
                          title={t("littleInfo.copyTitle")}
                        >
                          {contentCopied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 mt-5">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                      className={`
                        group relative overflow-hidden
                        flex items-center justify-center gap-2
                        w-full sm:w-auto
                        px-5 sm:px-6 py-2.5 rounded-xl
                        text-xs sm:text-sm font-bold text-white
                        transition-all duration-300
                        hover:scale-[1.02] active:scale-95
                        disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                        shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                        hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                        ${editId
                          ? "bg-gradient-to-l from-teal-700 to-emerald-500"
                          : "bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500"
                        }
                      `}
                    >
                      <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      {isSubmitting ? <WhiteLoader /> : editId ? <Save size={15} /> : <Plus size={15} />}
                      <span className="relative">
                        {editId ? t("littleInfo.edit") : t("littleInfo.add")}
                      </span>
                    </button>

                    {editId && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200"
                      >
                        <X size={15} />
                        {t("littleInfo.cancel")}
                      </button>
                    )}

                    {/* Download */}
                    <button
                      type="button"
                      onClick={handleOpenDownloadModal}
                      disabled={isDownloadDisabled()}
                      className={`
                        group flex items-center justify-center gap-2
                        w-full sm:w-auto sm:ms-auto
                        px-5 sm:px-6 py-2.5 rounded-xl m-0
                        text-xs sm:text-sm font-bold
                        transition-all duration-300
                        hover:scale-[1.02] active:scale-95
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                        ${isDownloadDisabled()
                          ? "text-slate-400 dark:text-slate-600 bg-slate-100/50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/60"
                          : "text-white bg-gradient-to-l from-blue-600 to-indigo-500 shadow-[0_8px_24px_-8px_rgba(59,130,246,0.8)] hover:shadow-[0_12px_32px_-8px_rgba(59,130,246,1)] border border-transparent"
                        }
                      `}
                    >
                      {isDownloading ? (
                        <WhiteLoader />
                      ) : hasDownloadedOnce ? (
                        <AlertTriangle size={15} />
                      ) : (
                        <Download size={15} />
                      )}
                      <span>
                        {hasDownloadedOnce
                          ? t("littleInfo.downloadDone")
                          : isDownloading
                            ? t("littleInfo.downloading")
                            : t("littleInfo.downloadData")}
                      </span>
                    </button>
                  </div>

                  {/* Status Row */}
                  <div className="mt-4 pt-4 border-t border-teal-500/10 dark:border-teal-400/10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] sm:text-xs">
                    <div className="flex items-center gap-1.5">
                      <Layers size={12} className="text-teal-600/80 dark:text-teal-300/80" />
                      <span className="text-slate-500 dark:text-slate-400">{t("littleInfo.chunksCount")}</span>
                      <span className="font-extrabold text-amber-600 dark:text-amber-300 tabular-nums">{list.length}</span>
                    </div>

                    <div className="w-px h-4 bg-teal-500/20 dark:bg-teal-400/20" />

                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 rounded-full ${hasDownloadedOnce ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" : "bg-emerald-400 shadow-[0_0_6px_rgba(16,185,129,0.8)]"}`}
                      />
                      <span className="text-slate-500 dark:text-slate-400">{t("littleInfo.downloadStatus")}</span>
                      <span className={`font-bold ${hasDownloadedOnce ? "text-amber-600 dark:text-amber-300" : "text-emerald-600 dark:text-emerald-300"}`}>
                        {hasDownloadedOnce ? t("littleInfo.statusDone") : t("littleInfo.statusReady")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ============ LIST CARD ============ */}
              <div className="max-w-5xl mx-auto relative rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">
                <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                </div>

                <div className="relative p-4 sm:p-5">
                  {/* List Header */}
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                        <ListChecks size={15} className="text-amber-400" />
                      </span>
                      <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-amber-100">
                        {t("littleInfo.listTitle")}
                      </h2>
                      <span className="text-[11px] font-bold text-teal-700 dark:text-teal-300 bg-teal-500/15 px-2 py-0.5 rounded-full tabular-nums border border-teal-500/30">
                        {t("littleInfo.itemCount", { count: sortedList.length })}
                      </span>
                    </div>
                  </div>

                  {isListLoading ? (
                    <div className="flex justify-center py-12">
                      <ColorRing
                        visible
                        height="40"
                        width="40"
                        ariaLabel="loading"
                        colors={["#14B8A6", "#0D9488", "#FCD34D", "#FBBF24", "#5EEAD4"]}
                      />
                    </div>
                  ) : (
                    <>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {paginatedList.map((item) => (
                          <li
                            key={item.id}
                            className={`
                              group relative
                              flex flex-col justify-between
                              p-4 rounded-xl
                              bg-white/60 dark:bg-white/5
                              border backdrop-blur-md
                              transition-all duration-200
                              hover:shadow-[0_8px_24px_-12px_rgba(15,118,110,0.5)]
                              ${editId === item.id
                                ? "border-amber-300/60 ring-2 ring-amber-300/30"
                                : "border-teal-500/15 dark:border-teal-400/15 hover:border-amber-300/40"
                              }
                            `}
                          >
                            <div className="flex-1 mb-3 min-w-0">
                              {item.keyword && (
                                <div className="flex items-start gap-2 mb-2">
                                  <span className="shrink-0 h-6 w-6 flex items-center justify-center rounded-md bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                                    <Key size={11} className="text-amber-500" />
                                  </span>
                                  <h3 className="text-sm sm:text-base font-bold text-slate-800 dark:text-amber-100 break-words">
                                    {item.keyword}
                                  </h3>
                                </div>
                              )}
                              {item.content && (
                                <p className="text-xs sm:text-[13px] text-slate-600 dark:text-slate-300 leading-6 break-words">
                                  {item.content}
                                </p>
                              )}

                              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                                {item.dataType && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-500/30 text-[10px] font-bold">
                                    <Layers size={9} />
                                    {DATA_TYPES.find((d) => d.id === item.dataType)?.name || item.dataType}
                                  </span>
                                )}
                                {(item.updatedAt || item.createdAt) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-500/10 text-slate-600 dark:text-slate-300 border border-slate-400/30 text-[10px] font-bold tabular-nums">
                                    <Calendar size={9} />
                                    {moment(item.updatedAt || item.createdAt)
                                      .locale("fa")
                                      .format("jYYYY/jMM/jDD HH:mm")}
                                  </span>
                                )}
                                {editId === item.id && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/15 text-amber-700 dark:text-amber-200 border border-amber-400/40 text-[10px] font-bold">
                                    <Pencil size={9} />
                                    {t("littleInfo.editing")}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 self-end opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                              <button
                                onClick={() => handleEdit(item)}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-teal-700 dark:text-teal-300 bg-teal-500/10 border border-teal-500/30 hover:bg-teal-500/20 hover:scale-105 active:scale-95 transition-all duration-200"
                                title={t("littleInfo.edit")}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                disabled={deletingId === item.id}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-400/40 hover:bg-rose-500/20 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50"
                                title={t("littleInfo.delete")}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {/* Pagination */}
                      {sortedList.length > ITEMS_PER_PAGE && (
                        <div className="flex justify-center items-center gap-1.5 mt-5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-105 active:scale-95"
                          >
                            <ChevronRight size={14} />
                          </button>

                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              type="button"
                              onClick={() => goToPage(page)}
                              className={`
                                min-w-[2.25rem] h-9 px-3 rounded-lg font-bold text-xs tabular-nums transition-all duration-200
                                ${page === currentPage
                                  ? "bg-gradient-to-l from-teal-700 to-teal-500 text-white shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]"
                                  : "bg-white/60 dark:bg-white/5 text-teal-700 dark:text-amber-200 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10"
                                }
                              `}
                            >
                              {page}
                            </button>
                          ))}

                          <button
                            type="button"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-105 active:scale-95"
                          >
                            <ChevronLeft size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!isListLoading && sortedList.length === 0 && (
                    <div className="text-center py-12">
                      <div className="h-14 w-14 mx-auto mb-3 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                        <BookOpen size={22} className="animate-pulse text-teal-400/70" />
                      </div>
                      <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                        {t("littleInfo.emptyList")}
                      </p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                        {t("littleInfo.emptyListHint")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* State indicator */}
              <div className="max-w-5xl mx-auto mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                <span className={`h-2 w-2 rounded-full ${editId ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" : "bg-teal-400 shadow-[0_0_6px_rgba(45,212,191,0.8)]"}`} />
                {editId ? t("littleInfo.stateEditing") : t("littleInfo.stateAdding")}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ================= Delete Confirm ================= */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-24 px-4 bg-slate-900/40 dark:!bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-white dark:!bg-slate-900 border border-rose-400/40 shadow-[0_25px_60px_-20px_rgba(244,63,94,0.6)]">
            <div className="h-[2px] bg-gradient-to-l from-transparent via-rose-400/80 to-transparent" />
            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-500">
                  <AlertTriangle size={16} />
                </span>
                <p className="text-sm font-bold text-slate-800 dark:text-rose-100">
                  {t("littleInfo.deleteConfirmTitle")}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingId === deleteTarget}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-l from-rose-700 to-rose-500 shadow-[0_6px_20px_-6px_rgba(244,63,94,0.8)] hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-60"
                >
                  {deletingId === deleteTarget ? <WhiteLoader /> : <Trash2 size={14} />}
                  {t("littleInfo.delete")}
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={deletingId === deleteTarget}
                  className="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-slate-700 dark:!text-slate-200 bg-slate-100 dark:!bg-slate-800 border border-slate-300/60 dark:!border-slate-700 hover:bg-slate-200 dark:hover:!bg-slate-700 transition-all duration-200 disabled:opacity-60"
                >
                  {t("littleInfo.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Download Modal ================= */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 dark:!bg-black/70 backdrop-blur-sm px-4">
          <div
            ref={modalRef}
            className="w-full max-w-md rounded-2xl overflow-hidden bg-white dark:!bg-slate-900 border border-teal-500/25 dark:!border-teal-400/20 shadow-[0_25px_60px_-20px_rgba(15,118,110,0.6)] dark:!shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)] animate-fadeIn"
          >
            <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

            <div className="p-5 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <FileSpreadsheet size={18} className="text-emerald-600 dark:text-emerald-300" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-800 dark:text-amber-100">
                      {t("littleInfo.modalTitle")}
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {t("littleInfo.modalTotalCount")}{" "}
                      <span className="font-bold text-amber-600 dark:text-amber-300 tabular-nums">
                        {list.length}
                      </span>{" "}
                      {t("littleInfo.modalItemUnit")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDownloadModal}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                {t("littleInfo.modalDescription")}
              </p>

              {/* Options */}
              <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-1">
                {DOWNLOAD_OPTIONS.map((option) => {
                  const isSelected = selectedCount === option.value;
                  const isDisabled = option.value !== null && option.value > list.length;

                  return (
                    <button
                      key={option.value ?? "all"}
                      onClick={() => !isDisabled && handleSelectCount(option.value, option.label)}
                      disabled={isDisabled}
                      className={`
                        w-full text-right px-3.5 py-3 rounded-xl transition-all duration-200
                        flex items-center justify-between gap-2 border
                        ${isSelected
                          ? "bg-teal-500/15 border-teal-400/60 text-teal-800 dark:text-teal-200 ring-2 ring-teal-400/30"
                          : "bg-white/60 dark:!bg-white/5 border-teal-500/15 dark:!border-teal-400/15 text-slate-700 dark:!text-slate-200 hover:border-amber-300/60 hover:bg-amber-300/5"
                        }
                        ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                      `}
                    >
                      <span className="font-semibold text-xs sm:text-sm">{option.label}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {option.value !== null && option.value > list.length && (
                          <span className="text-[10px] text-amber-500 font-bold">
                            {t("littleInfo.modalOnlyAvailable", { count: list.length })}
                          </span>
                        )}
                        {isSelected && (
                          <CheckCircle2 size={16} className="text-teal-500" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex gap-2.5 mt-5">
                <button
                  onClick={handleConfirmDownload}
                  disabled={selectedCount === null && !selectedCountLabel}
                  className="
                    flex-1 flex items-center justify-center gap-2
                    py-3 px-4 rounded-xl text-xs sm:text-sm font-bold text-white
                    bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                    shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                    hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                    hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                    hover:scale-[1.02] active:scale-95
                    transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  "
                >
                  {isDownloading ? <WhiteLoader /> : <Download size={14} />}
                  <span>
                    {t("littleInfo.modalDownloadSelected", {
                      label: selectedCountLabel || t("littleInfo.modalSelectedFallback"),
                    })}
                  </span>
                </button>

                <button
                  onClick={handleCloseDownloadModal}
                  className="px-5 py-3 rounded-xl text-xs sm:text-sm font-bold text-slate-700 dark:!text-slate-200 bg-slate-100 dark:!bg-slate-800 border border-slate-300/60 dark:!border-slate-700 hover:bg-slate-200 dark:hover:!bg-slate-700 transition-all duration-200"
                >
                  {t("littleInfo.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
}

const LittelInfo = () => (
  <SidebarProvider>
    <LittelInfoInner />
  </SidebarProvider>
);

export default LittelInfo;